import React, { useState, useEffect } from 'react';
import { Save, BarChart3 } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import FormModalLayout from '../Common/FormModalLayout';
import { ensureRichTextValue, RichTextEditor } from '../RichTextEditor';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const CompanySizeModal = ({ companySize, onClose, onSave }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { register, control, handleSubmit, formState: { errors }, setValue, reset } = useForm();

  const isEditing = !!companySize;

  useEffect(() => {
    if (companySize) {
      setValue('name', companySize.name);
      setValue('description', ensureRichTextValue(companySize.description || ''));
      setValue('minEmployees', companySize.minEmployees);
      setValue('maxEmployees', companySize.maxEmployees);
      setValue('isActive', companySize.isActive === true || companySize.isActive === 'true' ? 'true' : 'false');
    } else {
      reset({
        name: '',
        description: '',
        minEmployees: '',
        maxEmployees: '',
        isActive: 'true',
      });
    }
  }, [companySize, setValue, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving company size:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormModalLayout
      title={isEditing ? 'Editar tamanho de empresa' : 'Novo tamanho de empresa'}
      subtitle="Faixa de funcionários para classificação de porte."
      headerIcon={BarChart3}
      onClose={onClose}
      size="lg"
      historyEntityType="company_sizes"
      historyEntityId={companySize?.id}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={isLoading}>
            Cancelar
          </button>
          <Can permission={P.CATALOG_ACCESS}>
            <button
              type="submit"
              form="company-size-modal-form"
              className="btn btn-primary inline-flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
              ) : (
                <Save className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {isEditing ? 'Atualizar' : 'Salvar'} tamanho
            </button>
          </Can>
        </>
      }
    >
      <form id="company-size-modal-form" onSubmit={handleSubmit(onSubmit)} className="form-stack">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="form-group">
            <label className="form-label form-label-required">Nome</label>
            <input
              type="text"
              className="form-input"
              placeholder="Ex: Microempresa"
              {...register('name', { required: 'Nome é obrigatório' })}
            />
            {errors.name && <p className="form-error">{errors.name.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" {...register('isActive')}>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label form-label-required">Mínimo de funcionários</label>
            <input
              type="number"
              className="form-input"
              placeholder="1"
              {...register('minEmployees', {
                required: 'Mínimo de funcionários é obrigatório',
                min: { value: 1, message: 'Mínimo deve ser 1' },
              })}
            />
            {errors.minEmployees && <p className="form-error">{errors.minEmployees.message}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Máximo de funcionários</label>
            <input
              type="number"
              className="form-input"
              placeholder="Deixe vazio para sem limite"
              {...register('maxEmployees')}
            />
            <p className="form-help">Deixe vazio para sem limite superior</p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Descrição</label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                variant="compact"
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Descrição do tamanho de empresa..."
              />
            )}
          />
        </div>
      </form>
    </FormModalLayout>
  );
};

export default CompanySizeModal;
